import {Link as RouterLink} from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import {IconButton, Link, Tooltip} from '@mui/material';
import {isAxiosError} from 'axios';
import {MRT_ColumnDef} from 'material-react-table';
import {useSnackbar} from 'notistack';

import {useGetReports, UserReport, useUpdateReport} from '@entities/report';
import {UserHoverCard} from '@entities/user';
import {getErrorMessageFromApiError} from '@shared/api';
import {MrTable, TableRowActionsContainer} from '@shared/components';
import {
  DATETIME_FORMAT,
  entityTableCommonProps,
  ROUTE__USER_ACCOUNT,
} from '@shared/constants';
import {useUrlState} from '@shared/hooks';
import {formatInTz} from '@shared/utils';
import {Header} from '@widgets/common';

import {PaginationState} from '@/shared/types';

const localStorageKey = 'reportsTable';

type UrlState = PaginationState;

const columns: MRT_ColumnDef<UserReport>[] = [
  {
    accessorKey: 'reporterUsername',
    header: 'Reporter',
    size: 180,
    Cell: ({row}) => {
      const {reporterId, reporterUsername} = row.original;

      return (
        <UserHoverCard userId={reporterId}>
          <Link
            component={RouterLink}
            to={ROUTE__USER_ACCOUNT.replace(':id', reporterId)}
          >
            {reporterUsername}
          </Link>
        </UserHoverCard>
      );
    },
  },
  {
    accessorKey: 'reportedUsername',
    header: 'Reported user',
    size: 180,
    Cell: ({row}) => {
      const {reportedUserId, reportedUsername} = row.original;

      return (
        <UserHoverCard userId={reportedUserId}>
          <Link
            component={RouterLink}
            to={ROUTE__USER_ACCOUNT.replace(':id', reportedUserId)}
          >
            {reportedUsername}
          </Link>
        </UserHoverCard>
      );
    },
  },
  {
    accessorKey: 'cause',
    header: 'Cause',
    size: 320,
    grow: true,
    Cell: ({row}) => row.original.cause || '-',
  },
  {
    accessorKey: 'createdAt',
    header: 'Date and time',
    size: 180,
    Cell: ({row}) => formatInTz(row.original.createdAt, DATETIME_FORMAT),
  },
  {
    accessorKey: 'processed',
    header: 'Processed',
    size: 120,
    Cell: ({row}) => (row.original.processed ? 'Yes' : 'No'),
  },
];

export const ReportsPage = () => {
  const {enqueueSnackbar} = useSnackbar();
  const {urlState, setUrl} = useUrlState<UrlState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const {
    data,
    isPending: isLoading,
    error,
  } = useGetReports({
    page: urlState.pageIndex + 1,
    pageSize: urlState.pageSize,
  });

  const {mutateAsync: updateReport, isPending: isSaving} = useUpdateReport();

  const handleMarkProcessed = async (report: UserReport) => {
    try {
      await updateReport({reportId: report.id, processed: true});
      enqueueSnackbar('Report marked as processed', {variant: 'success'});
    } catch (err) {
      const errorMessage = isAxiosError(err)
        ? getErrorMessageFromApiError(err)
        : '';
      enqueueSnackbar(`Could not update report. ${errorMessage}`, {
        variant: 'error',
      });
    }
  };

  return (
    <MrTable
      error={error}
      columns={columns}
      data={data?.items ?? []}
      renderTopToolbarCustomActions={() => (
        <Header title="" isBackButtonShown={false} />
      )}
      renderRowActions={({row}) => (
        <TableRowActionsContainer>
          <Tooltip
            title={
              row.original.processed ? 'Already processed' : 'Mark as processed'
            }
          >
            <span>
              <IconButton
                color="success"
                disabled={row.original.processed || isSaving}
                onClick={() => handleMarkProcessed(row.original)}
              >
                <CheckIcon />
              </IconButton>
            </span>
          </Tooltip>
        </TableRowActionsContainer>
      )}
      enableColumnOrdering
      enableColumnDragging={false}
      enableColumnPinning
      localStorageKeyForSettings={localStorageKey}
      enableHiding
      enablePagination
      manualPagination
      onPaginationChange={setUrl}
      rowCount={data?.total ?? 0}
      state={{
        isLoading,
        pagination: urlState,
      }}
      initialState={{
        columnPinning: {
          left: ['reporterUsername'],
          right: ['mrt-row-actions'],
        },
      }}
      {...entityTableCommonProps}
    />
  );
};
